import { Box } from "@mui/material";
import { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGetRequest, apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import CommunitySuppliers from "./CommunitySuppliers";
import ConfirmationModal from "./ConfirmationModal";
import { useSnackbar } from "../../Components/SnackBar";
import SupplierProfileModal from "../SupplierProfileModal";
import PointerUtils from "../../Utils/pointer";
import { sortCategoriesWithSubcategories } from "../../Utils/SortData";

export default function MySuppliers() {
  const { t } = useTranslation(["page-my-suppliers", "categories"]);

  const [supplierStats, setSupplierStats] = useState({
    requestsStat: 1,
    invitesStat: 1,
    activeSuppliersStat: 1,
  });
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const showSnackbar = useSnackbar();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [showSupplierProfile, setShowSupplierProfile] = useState(false);
  const [viewType, setViewType] = useState("view");
  const [supplierProfileData, setSupplierProfileData] = useState(null);
  const [supplierStatusChanged, setSupplierStatusChanged] = useState([]);
  const [refreshSuppliersData, setRefreshSuppliersData] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const getSupplierStats = useCallback(async () => {
    try {
      const response = await apiGetRequest(
        endpoints.getSupplierManagementStats,
      );
      if (response.status === 200) {
        setSupplierStats({
          requestsStat: response?.data?.total_pending_offers,
          invitesStat: response?.data?.total_sent_invites,
          activeSuppliersStat: response?.data?.total_active_suppliers,
        });
      }
    } catch (ex) {
      console.log("exception occurred while getting stats", ex);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const categoriesResponse = await apiGetRequest(
        endpoints.getAllCategories,
      );
      let categories = categoriesResponse?.data || [];

      categories = sortCategoriesWithSubcategories(categories);

      // Sub-categories only. Listing a parent alongside its children made the
      // dropdown repeat itself — several parents have a same-named catch-all
      // child (e.g. "Beauty & Personal Care" exists as both), so the same label
      // appeared twice with no way to tell them apart. A top-level category
      // with no children is dropped too rather than kept as a special case.
      const categoriesList = categories.flatMap(
        (cat) =>
          cat.sub_categories?.map((sub) => ({
            id: sub.id,
            name: t(sub.name, { ns: "categories" }),
            display_order: sub.display_order,
          })) || [],
      );

      setCategories(categoriesList);
    } catch (ex) {
      console.log("exception occurred while getting categories", ex);
    }
  }, [t]);

  useLayoutEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const cancelInvitationRequest = async () => {
    try {
      setConfirmModalLoading(true);
      await apiPostRequest(
        endpoints.cancelSupplierInvite(
          selectedSupplier?.id ?? selectedSupplier?.supplier_id,
        ),
      );
      showSnackbar(
        `${selectedSupplier?.name ?? selectedSupplier?.supplier_name}${t(
          "ConfirmationModal.copy_invite_cancelled",
        )}`,
        "success",
      );
      setRefreshSuppliersData(true);
      handleCloseSupplierProfileModal();
    } catch (ex) {
      console.log("exception while cancelling request", ex);
    } finally {
      setSelectedSupplier(null);
      setIsConfirmModalOpen(false);
      setConfirmModalLoading(false);
    }
  };

  const handleAction = () => {
    if (actionType === "cancel") cancelInvitationRequest();
  };

  const handleConfirmModal = (supplier, action) => {
    setSelectedSupplier(supplier);
    setActionType(action);
    setIsConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setSelectedSupplier(null);
    setConfirmModalLoading(false);
  };

  const handleSupplierProfileModal = async (supplier, viewType) => {
    setSelectedSupplier(supplier);
    await getSupplierProfileData(supplier.supplier_id);
    setViewType(viewType);
    setShowSupplierProfile(true);
  };

  const getSupplierProfileData = async (supplier_id) => {
    try {
      PointerUtils.disable();
      const response = await apiGetRequest(
        endpoints.getSupplierProfilePricelists(supplier_id),
      );
      setSupplierProfileData(response?.data || {});
    } catch (err) {
      console.log("error: ", err);
      showSnackbar("Error fetching supplier profile data", "error"); // This message could be translated too, if desired
    } finally {
      PointerUtils.enable();
    }
  };

  const handleCloseSupplierProfileModal = () => {
    setShowSupplierProfile(false);
    setSelectedSupplier(null);
  };

  const getModalContent = () => {
    const name = selectedSupplier?.name ?? selectedSupplier?.supplier_name;

    switch (actionType) {
      case "remove":
        return {
          text: `${t("ConfirmationModal.text.remove_part1")}${name} ${t(
            "ConfirmationModal.text.remove_part2",
          )}`,
          okText: t("ConfirmationModal.ok_button_text.remove"),
        };
      case "accept":
        return {
          text: `${t("ConfirmationModal.text.accept")}${name}?`,
          okText: t("ConfirmationModal.ok_button_text.accept"),
        };
      default:
        return {
          title: t("ConfirmationModal.title.cancel_invite"),
          text: `${t("ConfirmationModal.text.cancel_invite_part1")}${name} ${t(
            "ConfirmationModal.text.cancel_invite_part2",
          )}`,
          okText: t("ConfirmationModal.ok_button_text.reject"),
        };
    }
  };

  const { title, text, okText } = getModalContent();

  return (
    <>
      <Box
        sx={{
          marginTop: {
            xs: "75px",
            md: "0px",
          },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <CommunitySuppliers
          {...{
            handleSupplierProfileModal,
            handleConfirmModal,
            supplierStatusChanged,
            refreshSuppliersData,
            setRefreshSuppliersData,
            supplierStats,
            getSupplierStats,
            categories,
          }}
        />

        {showSupplierProfile && supplierProfileData && (
          <SupplierProfileModal
            {...{
              viewType:
                viewType === "view"
                  ? supplierProfileData.is_invited === true
                    ? "view"
                    : "invite"
                  : viewType,
              openSupplierProfileModal: showSupplierProfile,
              supplierProfileData,
              handleCloseSupplierProfileModal,
              setSupplierStatusChanged,
              setRefreshSuppliersData,
              handleConfirmModal,
            }}
          />
        )}

        {isConfirmModalOpen && (
          <ConfirmationModal
            open={isConfirmModalOpen}
            actionType={actionType}
            title={title ?? text}
            text={text}
            okText={okText}
            cancelText={t("ConfirmationModal.cancel_button")}
            handleOpen={handleAction}
            handleClose={handleCloseConfirmModal}
            confirmModalLoading={confirmModalLoading}
          />
        )}
      </Box>
    </>
  );
}
